<script setup lang="ts">
import { isString } from 'lodash-es';
import getCaretPosition from 'textarea-caret';
import { computed, onMounted, onUnmounted, onUpdated, ref, watch, nextTick, reactive } from 'vue';
import { TagsProfile } from './transactions/types/receiptFile';
import { BeneficiarySummary } from './payments/types/beneficiary';

const props = defineProps<{
  mentionableItems: TagsProfile;

  omitKey?: boolean | false;

  filteringDisabled: boolean | false;

  insertSpace: boolean | false;

  allowingNewItems: boolean | true;

  limit: number | 5;

  mapInsert?: (item: BeneficiarySummary | undefined, key: string) => string;

  keys: string[];

  caretHeight: number | 0;
}>();

const emit = defineEmits(['search', 'mention', 'tag']);

const currentKey = ref<string>('');
let currentKeyIndex: number;
const oldKey = ref<string>('');

interface State {
  isMention: boolean;
}

const state: State = reactive({
  isMention: false
});

const searchText = ref<string>('');

watch(searchText, (value, oldValue) => {
  if (value) {
    emit('search', value, oldValue);
  }
});

const filteredMentionableItems = computed(() => {
  if (!props.mentionableItems.mentions) {
    return [];
  }
  if (!searchText.value || props.filteringDisabled) {
    return props.mentionableItems.mentions;
  }

  const finalSearchText = searchText.value.toLowerCase();
  return props.mentionableItems.mentions.filter((item) => {
    let text: string | null;
    if (item) {
      text = item;
    } else {
      text = '';
    }
    return text.toLowerCase().includes(finalSearchText);
  });
});

const filteredTagItems = computed(() => {
  if (!props.mentionableItems.tags) {
    return [];
  }
  if (!searchText.value || props.filteringDisabled) {
    return props.mentionableItems.tags;
  }

  const finalSearchText = searchText.value.toLowerCase();
  return props.mentionableItems.tags.filter((item) => {
    return item.toLowerCase().includes(finalSearchText);
  });
});

const displayedItems = computed(() => {
  if (state.isMention) {
    return filteredMentionableItems.value.slice(0, props.limit);
  } else {
    return filteredTagItems.value.slice(0, props.limit);
  }
});

// Selection

const selectedIndex = ref(0);

watch(displayedItems, () => {
  selectedIndex.value = 0;
});

// Input element management
let input: HTMLElement;
const el = ref<HTMLDivElement | undefined>(undefined);

function getInput() {
  if (el.value !== undefined) {
    return (
      el.value.querySelector('input') ??
      el.value.querySelector('textarea') ??
      el.value.querySelector('[contenteditable="true"]')
    );
  } else {
    return undefined;
  }
}

onMounted(() => {
  if (el.value !== undefined) {
    el.value = getInput() as HTMLDivElement;
  }
  attach();
});

onUpdated(() => {
  const newInput = getInput();
  if (newInput !== input) {
    detach();
    input = newInput as HTMLElement;
    attach();
  }
});

onUnmounted(() => {
  detach();
});

watch(searchText, (value, oldValue) => {
  if (value) {
    emit('search', value, oldValue);
  }
});

function attach() {
  if (input) {
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeyDown);
    input.addEventListener('keyup', onKeyUp);
    input.addEventListener('scroll', onScroll);
    input.addEventListener('blur', onBlur);
  }
}

function detach() {
  if (input) {
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeyDown);
    input.removeEventListener('keyup', onKeyUp);
    input.removeEventListener('scroll', onScroll);
    input.removeEventListener('blur', onBlur);
  }
}

function onInput() {
  checkKey();
}

function onBlur() {
  closeMenu();
}

function onKeyDown(e: KeyboardEvent) {
  if (currentKey.value) {
    if (e.key === 'ArrowDown') {
      selectedIndex.value++;
      if (selectedIndex.value >= displayedItems.value.length) {
        selectedIndex.value = 0;
      }
      cancelEvent(e);
    }
    if (e.key === 'ArrowUp') {
      selectedIndex.value--;
      if (selectedIndex.value < 0) {
        selectedIndex.value = displayedItems.value.length;
      }
      cancelEvent(e);
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && displayedItems.value.length > 0) {
      applyMention(selectedIndex.value);
      cancelEvent(e);
    }
    if (e.key === 'Escape') {
      closeMenu();
      cancelEvent(e);
    }
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') {
      addNewItem(state.isMention);
      closeMenu();
      cancelEvent(e);
    }
  }
}

let cancelKeyUp: string | null = null;

function onKeyUp(e: KeyboardEvent) {
  if (cancelKeyUp && e.key === cancelKeyUp) {
    cancelEvent(e);
  }
  cancelKeyUp = null;
}

function cancelEvent(e: KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
  cancelKeyUp = e.key;
}

function onScroll() {
  updateCaretPosition();
}

function getSelectionStart() {
  return input.isContentEditable ? window.getSelection()?.anchorOffset : (input as HTMLInputElement).selectionStart;
}

function setCaretPosition(index: number) {
  nextTick(() => {
    (input as HTMLInputElement).selectionEnd = index;
  });
}

function getValue() {
  return input.isContentEditable
    ? (window.getSelection()?.anchorNode?.textContent ?? '')
    : (input as HTMLInputElement).value;
}

function setValue(value: string) {
  (input as HTMLInputElement).value = value;
  emitInputEvent('input');
}

function emitInputEvent(type: string) {
  input.dispatchEvent(new Event(type));
}

let lastSearchText: string | null = null;

function checkKey() {
  const index = getSelectionStart();
  if (index != null && index >= 0) {
    const { key, keyIndex } = getLastKeyBeforeCaret(index);
    const text = (lastSearchText = getLastSearchText(index, keyIndex));
    if (!(keyIndex < 1 || /\s/.test(getValue()[keyIndex - 1]))) {
      return false;
    }
    if (text != null) {
      state.isMention = key === '@';
      isMention.value = state.isMention;
      openMenu(key, keyIndex);
      searchText.value = text;
      return true;
    }
  }
  closeMenu();
  return false;
}

function getLastKeyBeforeCaret(caretIndex: number) {
  const [keyData] = props.keys
    .map((key) => ({
      key,
      keyIndex: getValue()?.lastIndexOf(key, caretIndex - 1) ?? 0
    }))
    .sort((a, b) => b.keyIndex - a.keyIndex);
  return keyData;
}

function getLastSearchText(caretIndex: number, keyIndex: number) {
  if (keyIndex !== -1) {
    const text = getValue()?.substring(keyIndex + 1, caretIndex) ?? '';
    // If there is a space we close the menu
    if (!/\s/.test(text)) {
      return text;
    }
  }
  return null;
}

// Position of the popper

const caretPosition = ref<{ top: number; left: number; height: number } | null>(null);

function updateCaretPosition() {
  if (currentKey.value) {
    if (input.isContentEditable) {
      const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
      const inputRect = input.getBoundingClientRect();
      caretPosition.value = {
        left: rect != null ? rect.left - inputRect.left : 0,
        top: rect != null ? rect.top - inputRect.top : 0,
        height: rect?.height ?? 0
      };
    } else {
      const pos = getCaretPosition(input, currentKeyIndex);
      caretPosition.value = {
        ...pos,
        top: pos.top + 20
      };
    }
    if (caretPosition.value != null) {
      caretPosition.value.top -= input.scrollTop;
      if (props.caretHeight) {
        caretPosition.value.height = props.caretHeight;
      } else if (isNaN(caretPosition.value.height)) {
        caretPosition.value.height = 16;
      }
    }
  }
}

// Open/close
const isMenuOpen = ref(false);

// Mention/Tag
const isMention = ref(false);

function openMenu(key: string, keyIndex: number) {
  if (currentKey.value !== key) {
    currentKey.value = key;
    currentKeyIndex = keyIndex;
    updateCaretPosition();
    selectedIndex.value = 0;
    isMenuOpen.value = true;
  }
}

function closeMenu() {
  if (currentKey.value != null) {
    oldKey.value = currentKey.value;
    currentKey.value = '';
    isMenuOpen.value = false;
  }
}

// Add new item

function addNewItem(isMention: boolean) {
  emit(isMention ? 'mention' : 'tag', searchText.value);
}

// Apply

function applyMention(itemIndex: number) {
  const item = displayedItems.value[itemIndex];
  let value =
    (props.omitKey ? '' : currentKey.value) +
    String(props.mapInsert ? props.mapInsert(isString(item) ? undefined : item, currentKey.value) : item) +
    (props.insertSpace ? ' ' : '');
  if (input.isContentEditable) {
    const range = window.getSelection()?.getRangeAt(0);
    range?.setStart(
      range.startContainer,
      range.startOffset - currentKey.value.length - (lastSearchText ? lastSearchText.length : 0)
    );
    range?.deleteContents();
    range?.insertNode(document.createTextNode(value));
    range?.setStart(range.endContainer, range.endOffset);
    emitInputEvent('input');
  } else {
    setValue(replaceText(getValue() ?? '', searchText.value, value, currentKeyIndex));
    setCaretPosition(currentKeyIndex + value.length);
  }
  if (value.startsWith('@') || value.startsWith('#')) {
    value = value.slice(1);
  }
  if (state.isMention) {
    emit('mention', value);
  } else {
    emit('tag', value);
  }
  closeMenu();
}

function replaceText(text: string, searchString: string, newText: string, index: number) {
  return text.slice(0, index) + newText + text.slice(index + searchString.length + 1, text.length);
}
</script>

<template>
  <div ref="el" class="mentionable relative h-full" :class="$attrs.class">
    <slot />

    <div
      class="absolute z-40 gap-10 rounded-lg border border-yapdarkgray-200 bg-white shadow-lg dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800 dark:text-yapgray-200"
      :style="
        caretPosition
          ? {
              top: `${caretPosition.top}px`,
              left: `${caretPosition.left}px`
            }
          : {}
      "
      v-if="isMenuOpen"
    >
      <div v-if="!displayedItems.length">
        <slot v-if="$props.allowingNewItems" name="no-result"> {{ $t('dict.newItem') }} </slot>
        <slot v-else name="no-result"> {{ $t('dict.noItem') }} </slot>
      </div>

      <template v-else>
        <div
          v-for="(item, index) of displayedItems"
          :key="index"
          class="cursor-pointer p-2 hover:bg-yapdarkgray-500 hover:text-yapdarkgray-100 hover:dark:bg-yapdarkgray-300 hover:dark:text-primary-dark-950"
          :class="{
            ' bg-yapdarkgray-500 text-yapgray-100 dark:bg-yapdarkgray-300 dark:text-primary-dark-950':
              selectedIndex === index
          }"
          @mouseover="selectedIndex = index"
          @mousedown="applyMention(index)"
        >
          <slot :name="`item-${currentKey || oldKey}`" :item="item" :index="index">
            <slot name="item" :item="item" :index="index" v-if="typeof item === 'string'">
              {{ item }}
            </slot>
            <slot name="item" :item="item" :index="index" v-else>
              {{ item }}
            </slot>
          </slot>
        </div>
      </template>
    </div>
  </div>
</template>
